import { EARTH } from '@trajectory/astro';
import {
  MISSION_DESTINATIONS,
  WeatherSchema,
  type BodyIdDto,
  type CreateMission,
  type CrewKind,
  type LaunchSiteDto,
  type MissionType,
  type OrbitalElementsDto,
  type WeatherDto,
} from '@trajectory/contracts';

export interface WizardOrbitPreset {
  id: string;
  name: string;
  altitudeMeters: number;
  inclinationDeg: number | null;
  eccentricity: number;
  description: string;
}

export interface MissionWizardDraft {
  name: string;
  description: string;
  missionType: MissionType;
  destinationBody: BodyIdDto;
  orbitPresetId: string;
  launchAtLocal: string;
  launchSiteId: string;
  payloadMassKg: number;
  crewKind: CrewKind;
  crewCount: number;
  rocketId: string;
  tags: string;
  weatherEnabled: boolean;
  weather: WeatherDto;
}

export const missionTypeGuidance: Record<
  MissionType,
  { label: string; shortLabel: string; description: string }
> = {
  suborbital: {
    label: 'Suborbital flight',
    shortLabel: 'Suborbital',
    description: 'Reaches space but does not gain enough sideways speed to stay in orbit.',
  },
  leo: {
    label: 'Low Earth orbit (LEO)',
    shortLabel: 'LEO',
    description: 'An Earth orbit below about 2,000 km, used by the ISS and many satellites.',
  },
  meo: {
    label: 'Medium Earth orbit (MEO)',
    shortLabel: 'MEO',
    description: 'An Earth orbit between LEO and GEO, commonly used by navigation satellites.',
  },
  geo: {
    label: 'Geostationary orbit (GEO)',
    shortLabel: 'GEO',
    description: 'A 24-hour equatorial orbit where a satellite appears fixed above one longitude.',
  },
  lunar: {
    label: 'Lunar mission',
    shortLabel: 'Lunar',
    description: 'Travels to, around, or near the Moon.',
  },
  interplanetary: {
    label: 'Interplanetary mission',
    shortLabel: 'Interplanetary',
    description: 'Leaves Earth for another planet, usually during a favorable launch window.',
  },
  escape: {
    label: 'Earth escape',
    shortLabel: 'Escape',
    description: 'Gains enough energy to leave Earth without a bound return orbit.',
  },
};

export function suggestedDestination(missionType: MissionType): BodyIdDto {
  return missionType === 'interplanetary'
    ? 'mars'
    : MISSION_DESTINATIONS[missionType][0];
}

export function destinationsForMissionType(
  missionType: MissionType,
): readonly BodyIdDto[] {
  return MISSION_DESTINATIONS[missionType];
}

export function createMissionWizardDraft(
  firstLaunchSiteId: string,
  now = new Date(),
): MissionWizardDraft {
  const launch = new Date(now.getTime() + 24 * 60 * 60_000);
  launch.setUTCSeconds(0, 0);

  return {
    name: 'New Mission',
    description: '',
    missionType: 'leo',
    destinationBody: 'earth',
    orbitPresetId: '',
    launchAtLocal: launch.toISOString().slice(0, 16),
    launchSiteId: firstLaunchSiteId,
    payloadMassKg: 1000,
    crewKind: 'cargo',
    crewCount: 0,
    rocketId: '',
    tags: '',
    weatherEnabled: false,
    weather: WeatherSchema.parse({}),
  };
}

export function validateMissionWizardStep(
  draft: MissionWizardDraft,
  step: number,
  launchSites: readonly LaunchSiteDto[],
): string[] {
  const errors: string[] = [];

  if (step === 0) {
    if (!draft.name.trim()) errors.push('Give the mission a name.');
    if (draft.name.trim().length > 200) errors.push('Mission names can contain at most 200 characters.');
    if (draft.description.length > 8000) errors.push('The description can contain at most 8,000 characters.');
  }

  if (
    step === 1 &&
    !(MISSION_DESTINATIONS[draft.missionType] as readonly BodyIdDto[]).includes(
      draft.destinationBody,
    )
  ) {
    errors.push(
      `Choose a destination supported by the ${missionTypeGuidance[draft.missionType].label} profile.`,
    );
  }

  if (step === 2) {
    if (!launchSites.some((site) => site.id === draft.launchSiteId)) {
      errors.push('Choose a launch site.');
    }
    if (!draft.launchAtLocal || Number.isNaN(Date.parse(`${draft.launchAtLocal}:00Z`))) {
      errors.push('Choose a valid launch date and time.');
    }
  }

  if (step === 3) {
    if (
      !Number.isFinite(draft.payloadMassKg) ||
      draft.payloadMassKg < 0 ||
      draft.payloadMassKg > 10_000_000
    ) {
      errors.push('Payload mass must be between 0 and 10,000,000 kg.');
    }
    if (
      draft.crewKind === 'crew' &&
      (!Number.isInteger(draft.crewCount) || draft.crewCount < 1 || draft.crewCount > 20)
    ) {
      errors.push('Crewed missions must have between 1 and 20 people.');
    }
    if (parseTags(draft.tags).length > 20) errors.push('Use no more than 20 tags.');
  }

  return errors;
}

function parseTags(value: string): string[] {
  return [
    ...new Set(
      value
        .split(',')
        .map((tag) => tag.trim())
        .filter(Boolean),
    ),
  ].slice(0, 20);
}

function buildTargetOrbit(
  presetId: string,
  presets: readonly WizardOrbitPreset[],
  launchSite: LaunchSiteDto | undefined,
): OrbitalElementsDto | undefined {
  const preset = presets.find((item) => item.id === presetId);
  if (!preset) return undefined;

  return {
    semiMajorAxisMeters: EARTH.equatorialRadius + preset.altitudeMeters,
    eccentricity: preset.eccentricity,
    inclinationDeg: preset.inclinationDeg ?? Math.abs(launchSite?.latitudeDeg ?? 28.5),
    raanDeg: 0,
    argumentOfPeriapsisDeg: 0,
    trueAnomalyDeg: 0,
  };
}

export function buildMissionWizardPayload(
  draft: MissionWizardDraft,
  launchSites: readonly LaunchSiteDto[],
  orbitPresets: readonly WizardOrbitPreset[],
): CreateMission {
  const launchSite = launchSites.find((site) => site.id === draft.launchSiteId);
  const targetOrbit =
    draft.destinationBody === 'earth'
      ? buildTargetOrbit(draft.orbitPresetId, orbitPresets, launchSite)
      : undefined;

  return {
    name: draft.name.trim(),
    description: draft.description.trim(),
    missionType: draft.missionType,
    launchAt: new Date(`${draft.launchAtLocal}:00Z`).toISOString(),
    launchSiteId: draft.launchSiteId,
    destinationBody: draft.destinationBody,
    payloadMassKg: draft.payloadMassKg,
    crewKind: draft.crewKind,
    crewCount: draft.crewKind === 'crew' ? draft.crewCount : 0,
    rocketId: draft.rocketId || null,
    targetOrbit,
    weather: draft.weatherEnabled ? draft.weather : undefined,
    maneuvers: [],
    tags: parseTags(draft.tags),
  };
}
